import axios from 'axios';
import { Student, Class, CheckInResponse, CreateClassRequest, UpdateClassRequest } from '../context/types';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management utilities
const getToken = (): string | null => {
  return localStorage.getItem('auth_token');
};

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Clear stored auth data on 401
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      // Redirect to login by reloading the page
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

// API service class
export class CheckInApiService {
  /**
   * Fetch all students
   */
  static async getStudents(): Promise<Student[]> {
    const response = await apiClient.get<Student[]>('/students');
    return response.data;
  }

  /**
   * Fetch classes for a specific student
   */
  static async getStudentClasses(studentId: string): Promise<Class[]> {
    const response = await apiClient.get<Class[]>(`/students/${studentId}/classes?today`);
    return response.data.sort((a, b) => a.time < b.time ? -1 : 1);
  }

  /**
   * Submit check-in for student and selected classes
   */
  static async submitCheckIn(studentId: string, classIds: number[]): Promise<CheckInResponse> {
    const response = await apiClient.post<CheckInResponse>('/checkin', {
      student_id: studentId,
      class_ids: classIds,
    });
    return response.data;
  }

  /**
   * Fetch attendance report for a specific student
   */
  static async getStudentAttendance(
    studentId: string,
    startDate?: string,
    endDate?: string
  ): Promise<StudentAttendanceData> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const url = `/students/${studentId}/attendance${params.toString() ? '?' + params.toString() : ''}`;
    const response = await apiClient.get<StudentAttendanceData>(url);
    return response.data;
  }

  /**
   * Fetch attendance report for a specific class (by name)
   */
  static async getClassAttendance(
    className: string,
    startDate?: string,
    endDate?: string
  ): Promise<ClassAttendanceData> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const url = `/classes/${encodeURIComponent(className)}/attendance${params.toString() ? '?' + params.toString() : ''}`;
    const response = await apiClient.get<ClassAttendanceData>(url);
    return response.data;
  }

  /**
   * Fetch all classes (for class selector)
   */
  static async getClasses(): Promise<Class[]> {
    const response = await apiClient.get<Class[]>('/classes');
    return response.data;
  }

  /**
   * Create a new class (admin only)
   */
  static async createClass(data: CreateClassRequest): Promise<Class> {
    const response = await apiClient.post<Class>('/classes', data);
    return response.data;
  }

  /**
   * Update an existing class (admin only)
   */
  static async updateClass(id: number, data: UpdateClassRequest): Promise<Class> {
    const response = await apiClient.put<Class>(`/classes/${id}`, data);
    return response.data;
  }

  /**
   * Delete a class (admin only)
   */
  static async deleteClass(id: number): Promise<void> {
    await apiClient.delete(`/classes/${id}`);
  }

  /**
   * Sync students from GoHighLevel to database (admin only)
   */
  static async syncStudents(): Promise<SyncStudentsResponse> {
    const response = await apiClient.post<SyncStudentsResponse>('/students/sync');
    return response.data;
  }
}

// Types for attendance report
export interface AttendanceRecord {
  date: string;
  class_name: string;
  class_day: string | null;
  class_time: string | null;
}

export interface ClassSummary {
  class_name: string;
  count: number;
}

export interface StudentAttendanceData {
  student_id: string;
  student_name: string;
  student_rank: string | null;
  total_attendance: number;
  last_attendance_date: string | null;
  class_summaries: ClassSummary[];
  attendance_records: AttendanceRecord[];
}

// Types for class attendance report
export interface StudentRecord {
  date: string;
  student_name: string;
  student_rank: string | null;
}

export interface ClassInstanceSummary {
  day: string | null;
  time: string | null;
  count: number;
}

export interface ClassAttendanceData {
  class_name: string;
  total_attendance: number;
  last_attendance_date: string | null;
  class_instance_summaries: ClassInstanceSummary[];
  attendance_records: StudentRecord[];
}

// Type for sync students response
export interface SyncStudentsResponse {
  success: boolean;
  message: string;
  total: number;
  added: number;
  updated: number;
  removed: number;
}

// Export individual functions for easier use
export const getStudents = CheckInApiService.getStudents;
export const getStudentClasses = CheckInApiService.getStudentClasses;
export const submitCheckIn = CheckInApiService.submitCheckIn;
export const getStudentAttendance = CheckInApiService.getStudentAttendance;
export const getClassAttendance = CheckInApiService.getClassAttendance;
export const getClasses = CheckInApiService.getClasses;
export const createClass = CheckInApiService.createClass;
export const updateClass = CheckInApiService.updateClass;
export const deleteClass = CheckInApiService.deleteClass;
export const syncStudents = CheckInApiService.syncStudents;

export default CheckInApiService;